/**
 * Small helper to format numbers in compact form (1k, 2.4m, etc.)
 * - Keeps currency formatting when `currency` is provided
 * - Uses short compact display and lowercases unit letters (k, m, b)
 * - Keeps plain numbers unchanged below 1k
 */
export function formatCompact(value, opts = {}) {
  const num = Number(value) || 0;
  const { currency } = opts;

  // choose sensible fraction digits: show one decimal for compacted numbers
  const maxFractionDigits = Math.abs(num) >= 1000 ? 1 : 0;

  const nf = currency
    ? new Intl.NumberFormat('en-US', {
        notation: 'compact',
        compactDisplay: 'short',
        style: 'currency',
        currency,
        maximumFractionDigits: maxFractionDigits,
      })
    : new Intl.NumberFormat('en', {
        notation: 'compact',
        compactDisplay: 'short',
        maximumFractionDigits: maxFractionDigits,
      });

  let out = nf.format(num);

  // Normalise suffix letters to lowercase (K → k, M → m, ...)
  out = out.replace(/[KMBT]/g, (s) => s.toLowerCase());

  // Remove unnecessary `.0` before unit (e.g. `1.0k` → `1k`)
  out = out.replace(/\.0(?=[a-z])/i, '');

  return out;
}

/**
 * Format numbers for tonnes display - converts kg to tonnes
 * Shows compact notation (K) when tonnes >= 1000
 * Examples: 580 → "0.58", 15700 → "15.7", 1798670 → "1.8K"
 */
export function formatCompactTonnes(value) {
  const num = Number(value) || 0;
  
  // Convert kg to tonnes by dividing by 1000
  const tonnes = num / 1000;


  
  // If tonnes is 1000 or more, use compact notation with K/M/B
  if (tonnes >= 1000) {
    const nf = new Intl.NumberFormat('en', {
      notation: 'compact',
      compactDisplay: 'short',
      maximumFractionDigits: 1,
    });
    
    let out = nf.format(tonnes);
    // Normalize to lowercase (K → k, M → m, etc.)
    out = out.replace(/[KMBT]/g, (s) => s.toLowerCase());
    // Remove unnecessary .0 before unit
    out = out.replace(/\.0(?=[a-z])/i, '');
    return out;
  }
  
  // For values < 1000 tonnes, just show the number without suffix
  const nf = new Intl.NumberFormat('en', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

  return nf.format(tonnes);
}
